import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  IconButton,
  TextField,
  Button,
  Modal,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TablePagination,
  CircularProgress,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { BASE_URL } from "../../services/configUrls";

const FileManager = () => {
  const [domainId, setDomainId] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const [previewImg, setPreviewImg] = useState(null);       // presigned URL for modal
  const [previewLoading, setPreviewLoading] = useState(false); // loading state for image fetch

  // Pagination states
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Confirmation dialog states
  const [openConfirm, setOpenConfirm] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  // Fetch files with pagination
  const fetchItems = async (currentPage = page, currentLimit = rowsPerPage) => {
    try {
      const res = await axios.get(`${BASE_URL}/internship/get_files`, {
        params: {
          limit: currentLimit,
          offset: currentPage * currentLimit,
        },
      });

      const data = res.data;

      let fetched = [];
      if (Array.isArray(data)) {
        fetched = data;
      } else if (Array.isArray(data?.items)) {
        fetched = data.items;
      } else if (Array.isArray(data?.data)) {
        fetched = data.data;
      } else if (Array.isArray(data?.files)) {
        fetched = data.files;
      } else {
        console.warn("Unexpected API response shape:", data);
        fetched = [];
      }

      setItems(fetched);
      setTotal(data?.total ?? fetched.length);
    } catch (err) {
      console.error("Failed to fetch files:", err);
      setItems([]);
    }
  };

  useEffect(() => {
    fetchItems(page, rowsPerPage);
  }, [page, rowsPerPage]);

  // Handle page change
  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleRowsPerPageChange = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    setRowsPerPage(newLimit);
    setPage(0);
  };

  // ✅ Fetch presigned URL from S3 and open modal
  const handleViewImage = async (fileKey) => {
    try {
      setPreviewLoading(true);
      setPreviewImg(null); // clear previous image
      // Open modal immediately to show spinner
      setPreviewImg("loading");

      const res = await axios.post(`${BASE_URL}/get-image-from-s3`, {
        file_key: fileKey,  // sends { "file_key": "path/to/file.jpg" }
      });

      setPreviewImg(res.data.url); // ✅ set the presigned URL
    } catch (err) {
      console.error("Failed to fetch image from S3:", err);
      setPreviewImg(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  // Upload file
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) return;

    const formData = new FormData();
    formData.append("domain_id", domainId);
    formData.append("description", description);
    formData.append("file", file);

    try {
      setLoading(true);
      await axios.post(`${BASE_URL}/internship/files`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setDomainId("");
      setDescription("");
      setFile(null);
      setPage(0);
      fetchItems(0, rowsPerPage);
    } catch (err) {
      console.error("Upload failed:", err);
    } finally {
      setLoading(false);
    }
  };

  // Delete API
  const handleDelete = async (id) => {
    try {
      await axios.delete(`${BASE_URL}/internship/delete_file/${id}`);
      const isLastItemOnPage = items.length === 1 && page > 0;
      if (isLastItemOnPage) {
        setPage((prev) => prev - 1);
      } else {
        fetchItems(page, rowsPerPage);
      }
    } catch (err) {
      console.error("Delete failed:", err);
    }
  };

  // Close modal and clean up
  const handleClosePreview = () => {
    setPreviewImg(null);
    setPreviewLoading(false);
  };

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      {/* Upload Form */}
      <Paper className="p-4 space-y-4">
        <h2 className="text-xl font-semibold">Upload File</h2>

        <form
          onSubmit={handleSubmit}
          className="flex flex-col md:flex-row gap-4 items-start md:items-end"
        >
          <TextField
            label="Domain ID"
            type="number"
            size="small"
            value={domainId}
            onChange={(e) => setDomainId(e.target.value)}
            required
          />

          <TextField
            label="Description"
            size="small"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />

          <Button
            variant="contained"
            component="label"
            sx={{ bgcolor: "blue !important" }}
          >
            Select File
            <input
              type="file"
              hidden
              onChange={(e) => setFile(e.target.files[0])}
            />
          </Button>

          <Button
            variant="contained"
            type="submit"
            disabled={loading}
            sx={{ bgcolor: "blue !important" }}
          >
            {loading ? "Uploading..." : "Upload"}
          </Button>
        </form>

        {file && (
          <p className="text-sm text-gray-600 mt-1">
            Selected: <span className="font-medium">{file.name}</span>
          </p>
        )}
      </Paper>

      {/* Files Table */}
      <Paper className="p-4">
        <h2 className="text-xl font-semibold mb-3">
          Files List{" "}
          <span className="text-sm font-normal text-gray-500">
            ({total} total)
          </span>
        </h2>

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell><strong>ID</strong></TableCell>
              <TableCell><strong>Domain ID</strong></TableCell>
              <TableCell><strong>Description</strong></TableCell>
              <TableCell><strong>File</strong></TableCell>
              <TableCell align="right"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {!Array.isArray(items) || items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No items found
                </TableCell>
              </TableRow>
            ) : (
              items.map((item) => (
                <TableRow key={item.id} hover>
                  <TableCell>{item.id}</TableCell>
                  <TableCell>{item.domain_id}</TableCell>
                  <TableCell>{item.description || "—"}</TableCell>

                  {/* ✅ On click → call S3 presigned URL API */}
                  <TableCell
                    className="text-blue-600 underline cursor-pointer"
                    onClick={() => handleViewImage(item.file_path)}
                  >
                    View Image
                  </TableCell>

                  <TableCell align="right">
                    <IconButton
                      size="small"
                      sx={{ color: "blue !important" }}
                      onClick={() => {
                        setDeleteId(item.id);
                        setOpenConfirm(true);
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        {/* Pagination Controls */}
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={handlePageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Paper>

      {/* IMAGE PREVIEW MODAL */}
      <Modal open={!!previewImg} onClose={handleClosePreview}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "white",
            boxShadow: 24,
            p: 2,
            maxWidth: "90vw",
            maxHeight: "90vh",
            borderRadius: 2,
            overflow: "auto",
            minWidth: 200,
            minHeight: 100,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {/* ✅ Show spinner while fetching presigned URL */}
          {previewLoading || previewImg === "loading" ? (
            <CircularProgress sx={{ color: "blue" }} />
          ) : (
            <img
              src={previewImg}           // ✅ direct presigned S3 URL
              alt="Preview"
              style={{ width: "100%", height: "auto", display: "block" }}
            />
          )}
        </Box>
      </Modal>

      {/* DELETE CONFIRMATION DIALOG */}
      <Dialog open={openConfirm} onClose={() => setOpenConfirm(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>

        <DialogContent>
          Are you sure you want to delete this file?
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setOpenConfirm(false)}
            sx={{ color: "blue !important" }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            sx={{ bgcolor: "blue !important" }}
            onClick={() => {
              handleDelete(deleteId);
              setOpenConfirm(false);
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default FileManager;
